/**
 * UserMenuMobile Component
 *
 * A mobile-responsive user menu that displays user information and navigation options
 * in a drawer interface. This component integrates with Okta authentication and provides
 * access to key user actions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.switchE1E2DestinationForm - Callback function to handle E1/E2 destination form submission
 * @param {Function} props.logout - Callback function to handle user logout
 * @returns {React.ReactElement} A drawer-based mobile menu with user profile and navigation options
 *
 * Features:
 * - Displays authenticated user's name and email
 * - Three-dot icon to toggle drawer open/close
 * - User avatar icon
 * - Quick access to E1/E2 destination form
 * - Logout functionality
 *
 * @example
 * <UserMenuMobile
 *   switchE1E2DestinationForm={handleE1E2Switch}
 *   logout={handleLogout}
 * />
 */

import { useState, useMemo } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { Button, Heading } from '@americanexpress/dls-react'
import { Drawer } from '@americanexpress/dls-react'
import styles from './scss/UserMenu.module.scss'

export default function UserMenuMobile({ switchE1E2DestinationForm, logout }) {
  const [isDrawerOpen, openDrawer] = useState(false)

  const { authState } = useOktaAuth()

  const [userName, userEmail] = useMemo(
    () =>
      authState?.isAuthenticated
        ? [authState?.idToken?.claims?.name, authState?.idToken?.claims?.email]
        : ['', ''],
    [authState]
  )

  return (
    <>
      <i className={styles.threeDotIcon} onClick={() => openDrawer(true)} />
      <Drawer
        headingSlot={
          <Heading level={2} variant='sans-small-bold' className={styles.userMenuMobileHeading}>
            <div className={styles.userAvatar}>
              <i className={`${styles.userIcon} ${styles.mobileViewIcon}`} />
            </div>

            <div>
              <span title={userName}>{userName}</span>
              <span title={userEmail}>{userEmail}</span>
            </div>
          </Heading>
        }
        onClose={() => openDrawer(false)}
        showHeadingBackground
        variant='compact'
        isOpen={isDrawerOpen}
      >
        <ul className={`${styles.userMenuList} ${styles.userMenuMobileList}`}>
          <li
            onClick={() => {
              switchE1E2DestinationForm()
              openDrawer(false)
            }}
          >
            Submit E1/E2 Destination
          </li>
          <li onClick={() => logout()}>Logout</li>
        </ul>
      </Drawer>
    </>
  )
}
